/*
# -----------------------------------------------------------------------------
# Component: accountsAjaxCalls.js
# Author: antonio.ramirez@pst.sg
# Created Date: 02/April/2019
# Comments: Responsible for all backend related calls for Manage Accounts
# -----------------------------------------------------------------------------
*/
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate, formatDisplayDateTime2 } from '@/utils/date'
import clone from 'clone'
import { replaceWithBlank, replaceWithBlk, formatPrice, replaceBWithBlank } from '@/utils/index'
import { GLTRAN_DETAIL } from '@/store/types'

export const accountsAjaxCalls = {
  data() {
    return {
      actionType: undefined,
      tranType2: '',
      matchItems: [],
      riMatchItems: []
    }
  },
  computed: {
    glDetail() {
      return this.$store.state.accounts.glDetail
    },
    acctMaster() {
      return this.$store.state.accounts.acctMaster
    }
  },
  methods: {
    async performSearch() {
      const apiUrl = this.$store.state.accounts.menu.performSearch
      if (apiUrl) {
        try {
          if (this.accountsTransForm.actionName === null || this.accountsTransForm.actionName === '') {
            this.accountsTransForm.actionName = 'ACCT_MASTER_FILTER'
          }
          this.loading = true
          const { page, itemsPerPage } = this.getPagination()
          const paramMap = {
            page: page - 1,
            size: itemsPerPage
          }
          this.accountsTransForm.acctPageNo = this.paginationAM.page
          this.accountsTransForm.acctPageSize = this.paginationAM.itemsPerPage
          let messageFilters = clone(this.accountsTransForm)

          if (messageFilters.transactionDate !== null && messageFilters.transactionDate !== '') {
            messageFilters.transactionDate = formatDisplayDate(messageFilters.transactionDate)
          }

          if (messageFilters.dueDate !== null && messageFilters.dueDate !== '') {
            messageFilters.dueDate = formatDisplayDate(messageFilters.dueDate)
          }

          if (messageFilters.lastMatchedDate !== null && messageFilters.lastMatchedDate !== '') {
            messageFilters.lastMatchedDate = formatDisplayDate(messageFilters.lastMatchedDate)
          }

          const result = await this.$http.get(
            filterQuery(expandURI(apiUrl, paramMap), messageFilters, this.getPagination().sortBy, this.getPagination().sortDesc)
          )
          const { data } = result.data
          if (data) {
            this.resetResultItems()
            this.setTotalItems(result)
            data.map(result => {
              const resultList = {
                acctNo: result.acctNo,
                branch: result.branch,
                claimNo: result.claimNo,
                curr: result.curr,
                docNo: result.docNo,
                fund: result.fund,
                netAmount: formatPrice(result.netAmount),
                osBalance: formatPrice(result.osBalance),
                policyNo: result.policyNo,
                suffix: result.suffix,
                tranDate: this.formatDate(result.tranDate),
                rbcTran: result.rbcTran,
                type: result.type,
                type2: replaceWithBlank(result.type2),
                tranGrp: result.tranGrp,
                createdProgId: result.createdProgId,
                actions: result.actions,
                postNow: result.postNow,
                transactions: result.transactions,
                bankListPrtd: result.bankListPrtd,
                bankPayInPrtd: result.bankPayInPrtd,
                brnDocListPrtd: result.brnDocListPrtd,
                chequePrtd: result.chequePrtd,
                docPrtd: result.docPrtd,
                void_: result.void_,
                bankTranDate: this.formatDate(result.bankTranDate),
                zeroBal: result.zeroBal,
                bchMch: result.bchMch,
                glJrnl: result.glJrnl,
                glItemNo: result.glItemNo,
                glAcctNo: result.glAcctNoMix,
                glAmount: formatPrice(result.glAmt),
                glLocAmt: formatPrice(result.glLocAmt),
                glPeriod: result.glPeriod,
                mdJournalNo: result.mdJrnl,
                mdDate: this.formatDate(result.mdDate),
                mdBaseTrans: result.mdConTranTypeDocNo,
                mdSelectTrans: result.mdTranTypeDocNo,
                mdAmt: formatPrice(result.mdAmt),
                mdLocAmt: formatPrice(result.mdLocAmt),
                mdMchBy: result.mdMchBy,
                mdMchProg: result.mdMchProg,
                mdAcctNo: result.mdAcctNo,
                mdTranType: result.mdTranType,
                mdFund: result.mdFund,
                mdAcctNoSfx: result.mdAcctNoSfx,
                mdDocNo: result.mdDocNo,
                mdContTranType: result.mdContTranType,
                mdContDocNo: result.mdContDocNo,
                mdContJrnlNo: result.mdContJrnlNo,
                mdContBrn: result.mdContBrn,
                cmType: result.cmType,
                cmVoucher: result.cmVoucher,
                cmBillAcctNo: result.cmBillAcctNo,
                cmOvrdAcctNo: result.cmOvrdAcctNo,
                cmPolRenEndt: result.cmPolNo,
                cmCommAmt: formatPrice(result.cmCommAmt),
                cmPaidDate: this.formatDate(result.cmPaidDate),
                cmPaidBy: result.cmPaidBy,
                cmOvrdBrn: result.cmOvrdBrn,
                cmOvrdTranType: result.cmOvrdTranType,
                cmOvrdDocNo: result.cmOvrdDocNo,
                cmOvrdFund: result.cmOvrdFund,
                cmOvrdAcctNoSfx: result.cmOvrdAcctNoSfx,
                cmPolNo: result.polNo,
                cmRenCnt: result.renCnt,
                cmEndtCnt: result.endtCnt,
                ntBranch: result.branch,
                ntType: result.type,
                ntDocNo: result.docNo,
                ntAddrTo: result.addressedTo,
                ntFund: result.fund,
                ntSuffix: result.suffix,
                ntTranDate: this.formatDate(result.tranDate),
                ntCurr: result.curr,
                ntNetAmt: formatPrice(result.netAmount),
                ntLocal: formatPrice(result.locAmt),
                insdName: result.insdName
              }
              this.setResultItems(resultList)
            })
          } else {
            this.resetResultItems()
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        } finally {
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        this.loading = false
      }
    },
    async performAction(actionName, paramObject) {
      try {
        const apiUrl = this.$store.state.accounts.menu.performAction
        let requestParams = {}
        if (paramObject !== undefined) {
          requestParams = {
            actionName: actionName,
            branch: paramObject.mdBranch,
            accountNo: paramObject.mdAcctNo,
            transactionType: paramObject.mdType,
            documentNo: paramObject.mdDocNo,
            fund: paramObject.mdFund,
            accountNoSuffix: paramObject.mdSuffix
          }
        } else {
          requestParams = {
            actionName: actionName,
            branch: this.acctMaster.branch,
            accountNo: this.acctMaster.acctNo,
            transactionType: this.acctMaster.tranType,
            documentNo: this.acctMaster.docNo,
            fund: this.acctMaster.fund,
            accountNoSuffix: this.acctMaster.suffix
          }
        }
        if (apiUrl) {
          const response = await this.$http.post(apiUrl, requestParams)
          const { data } = response.data
          if (data) {
            this.accounts.branch = replaceWithBlank(data.accounts.branch)
            this.accounts.acctNoDesc = replaceWithBlank(data.accounts.acctNoDesc)
            this.accounts.branchDesc = replaceWithBlank(data.accounts.branchDesc)
            this.accounts.documentNoLbl = replaceWithBlank(data.accounts.docNoViewLbl)
            this.accounts.documentNo = replaceWithBlank(data.accounts.docNoView)
            this.accounts.transactionDate = replaceWithBlank(formatDisplayDate(data.accounts.tranDate))
            this.accounts.posted = replaceWithBlank(data.accounts.posted)
            this.accounts.accountNo = replaceWithBlank(replaceWithBlank(data.accounts.acctNo))
            this.accounts.fundSuffix = replaceWithBlank(data.accounts.fund) + ', ' + replaceWithBlank(data.accounts.suffix)
            this.accounts.department = replaceWithBlank(data.accounts.dept)
            this.accounts.transactionGroup = replaceWithBlank(data.accounts.tranGrp)
            this.accounts.gstRegistrationNo = replaceWithBlank(data.accounts.gstRegNo)
            this.accounts.gstIOStatusRate =
              replaceBWithBlank(data.accounts.inOutGST) + ', ' + replaceWithBlank(data.accounts.gstStat) + ', ' + replaceWithBlk(data.accounts.gstRate)
            this.accounts.currency = replaceWithBlank(data.accounts.curr)
            this.accounts.rbcTran = replaceWithBlank(data.accounts.rbcTran)
            this.accounts.netAmount = replaceWithBlank(formatPrice(data.accounts.netAmount))
            this.accounts.locAmtAe = data.accounts.locAmtAe === false ? 'E' : ''
            this.accounts.localNetAmount = replaceWithBlank(formatPrice(data.accounts.locAmt))
            this.accounts.outstandingBalance = replaceWithBlank(formatPrice(data.accounts.osBalance))
            this.accounts.localOutstandingBalance = replaceWithBlank(formatPrice(data.accounts.locOsBal))
            this.accountsLabel.debitAmount = replaceWithBlank(data.accounts.debitAmtLbl)
            this.accountsLabel.debitGST = replaceWithBlank(data.accounts.debitGSTLbl)
            this.accounts.debitAmount = replaceWithBlank(formatPrice(data.accounts.debitAmt))
            this.accounts.debitGST = replaceWithBlank(formatPrice(data.accounts.debitGST))
            this.accounts.instalmentNo = replaceWithBlank(data.accounts.instalmentNoOf)
            this.accounts.dueDate = replaceWithBlank(formatDisplayDate(data.accounts.dueDate))
            this.accounts.createdBy = replaceWithBlank(data.accounts.createdByName) + ' on ' + replaceWithBlank(data.accounts.createdDate)
            this.accounts.createdProgram = replaceWithBlank(data.accounts.createdProgId)
            this.accounts.lastModifiedBy = replaceWithBlank(data.accounts.modifiedByName) + ' on ' + replaceWithBlank(data.accounts.modDate)
            this.accounts.modifiedProgram = replaceWithBlank(data.accounts.modifiedProgId)
            this.accounts.zeroBal = data.accounts.zeroBal
            this.accounts.voidAe = data.accounts.void_
            this.accounts.voidReason = data.accounts.voidReason
            this.accounts.voidDate = formatDisplayDate(data.accounts.voidDate)
            this.accounts.fund = replaceWithBlank(data.accounts.fund)
            this.accounts.acctNoSfx = replaceWithBlank(data.accounts.suffix)
            this.accounts.gstStat = replaceWithBlank(data.accounts.gstStat)
            this.accounts.inOutGST = replaceBWithBlank(data.accounts.inOutGST)
            this.accounts.gstRate = replaceWithBlk(data.accounts.gstRate)

            // Match Details
            this.matchedDet.lastMatchedDate = replaceWithBlank(formatDisplayDate(data.accounts.lastMatchDate))
            this.matchedDet.lastBaseTranType = replaceWithBlank(data.accounts.lastBaseTranType)
            this.matchedDet.bchMatchDocNo =
              replaceWithBlank(data.accounts.bchMatchBranch) +
              ' ' +
              replaceWithBlank(data.accounts.bchMatchTranTye) +
              ' ' +
              replaceWithBlank(data.accounts.bchMatchDocNo) +
              ' ' +
              replaceWithBlank(data.accounts.bchMatchFund) +
              ' ' +
              replaceWithBlank(data.accounts.bchMatchAcctNoSuffix)
            this.matchedDet.bchMatchLater = replaceWithBlank(data.accounts.bchMatchLater)

            // Bank and GL Details
            this.bankDet.bankNo = replaceWithBlank(data.accounts.bankNo)
            this.bankDet.bankDesc = replaceWithBlank(data.accounts.bankDesc)
            this.bankDet.bankAcctCurr = replaceWithBlank(data.accounts.bankAcctCurr)
            this.bankDet.bankOrigAmt = replaceWithBlank(formatPrice(data.accounts.bankOrigAmt))
            this.bankDet.bankCharges = replaceWithBlank(formatPrice(data.accounts.bankChrg))
            this.bankDet.bankLocTotalLbl = replaceWithBlank(data.accounts.bankLocTotalLbl)
            this.bankDet.bankLocTotalAe = replaceWithBlank(data.accounts.bankLocTotalAe)
            this.bankDet.bankLocTotal = replaceWithBlank(formatPrice(data.accounts.bankLocTotal))
            this.bankDet.bankTranDate = replaceWithBlank(data.accounts.bankTranDate)
            this.bankDet.paymentMethod = replaceWithBlank(data.accounts.paymentMethod)
            this.bankDet.creditCardNo = replaceWithBlank(data.accounts.creditCardNo)
            this.bankDet.payerBank = replaceWithBlank(data.accounts.payerBank)
            this.bankDet.payerCheqNo = replaceWithBlank(data.accounts.payerCheqNo)
            this.bankDet.cheqNo = replaceWithBlank(data.accounts.cheqNo)
            this.bankDet.printCheq = replaceWithBlank(data.accounts.prtCheque) + ', ' + replaceWithBlank(data.accounts.cheqPrint)
            this.bankDet.cashBookNo = replaceWithBlank(data.accounts.cashBookNo)
            this.bankDet.cheqpayinSlipNoNo = replaceWithBlank(data.accounts.payInSlipNo)
            this.bankDet.drawerName = replaceWithBlank(data.accounts.drawerName)
            this.bankDet.glInterface = replaceWithBlank(data.accounts.glInterface)
            this.bankDet.acctPeriod = replaceWithBlank(data.accounts.acctPeriod)
            this.bankDet.bankOrigAmtAe = data.accounts.bankOrigAmtAe === false ? 'E' : ''

            // Policy Details
            this.policyDet.polType = replaceWithBlank(data.accounts.polType)
            this.policyDet.polNo = replaceWithBlank(data.accounts.polRenEndt)
            this.policyDet.commDate = formatDisplayDate(data.accounts.comDate)
            this.policyDet.coverNoteNo = replaceWithBlank(data.accounts.cnNo)
            this.policyDet.premium = replaceWithBlank(data.accounts.premium)
            this.policyDet.premiumGST = replaceWithBlank(data.accounts.premGST)
            this.policyDet.commission = formatPrice(data.accounts.commission)
            this.policyDet.commissionGST = replaceWithBlank(data.accounts.commGST)
            this.policyDet.commissionGSTStatRate = replaceWithBlank(data.accounts.gstStatComm) + ' ' + formatPrice(data.accounts.gstRateComm)
            this.policyDet.commissionStatus = replaceWithBlank(data.accounts.docStat)
            this.policyDet.fee = formatPrice(data.accounts.fee)
            this.policyDet.stamp = formatPrice(data.accounts.stamp)
            this.policyDet.tax = formatPrice(data.accounts.tax)
            this.policyDet.vehicleRegNo = replaceWithBlank(data.accounts.vehRegNo)
            this.policyDet.insuredRef = replaceWithBlank(data.accounts.insdRefNo)
            this.policyDet.insuredName = replaceWithBlank(data.accounts.insdName)
            this.policyDet.cessionNo = replaceWithBlank(data.accounts.cesNo)
            this.policyDet.cessionEndtNo = replaceWithBlank(data.accounts.cesEndt)
            this.policyDet.cedant = replaceWithBlank(data.accounts.cedant)
            this.policyDet.cessionPolNo = replaceWithBlank(data.accounts.cesPolNo)
            this.policyDet.cedantCessionNo = replaceWithBlank(data.accounts.cedCesNo)
            this.policyDet.claimNo = replaceWithBlank(data.accounts.clmNo)
            this.policyDet.claimAmt = formatPrice(data.accounts.clmAmt)
            this.policyDet.claimGST = formatPrice(data.accounts.clmGST)

            // Document details
            this.documentDet.documentTo = replaceWithBlank(data.accounts.docTo)
            this.documentDet.toPrint = replaceWithBlank(data.accounts.toPrint)
            this.documentDet.addressedTo = replaceWithBlank(data.accounts.addressedTo)
            this.documentDet.mailingAddress = replaceWithBlank(data.accounts.mailAddr)
            this.documentDet.remarks = replaceWithBlank(data.accounts.remarks)
            this.documentDet.statementRef = replaceWithBlank(data.accounts.stateRef)
            this.documentDet.debitNoteLbl = replaceWithBlank(data.accounts.debitNoteLbl)
            this.documentDet.debitNote = replaceWithBlank(data.accounts.debitNote)
            this.documentDet.bankList = replaceWithBlank(data.accounts.bankListPrtd)
            this.documentDet.bankPayinList = replaceWithBlank(data.accounts.bankPayInPrtd)
            this.documentDet.branchDocumentList = replaceWithBlank(data.accounts.brnDocListPrtd)
            this.documentDet.taxInvoiceNo = replaceWithBlank(data.accounts.taxInvNo)
            this.documentDet.printTaxInvoice = replaceWithBlank(data.accounts.taxInv)
            this.documentDet.theirTaxInvoiceNo = replaceWithBlank(data.accounts.thTaxInvNo)
            this.documentDet.tranType2 = replaceWithBlank(data.accounts.tranType2)
            if (paramObject !== undefined) {
              // AR: Added for UndoMatched OK-198
              this.processingFlag = false
            }
          }
        } else {
          if (paramObject !== undefined) {
            // AR: Added for UndoMatched OK-198
            this.processingFlag = false
          }
          this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        }
      } catch (error) {
        // if (paramObject !== undefined) {
        const { data } = error.response
        if (data) {
          this.message(data)
        }
        // }
      }
    },
    resetResultItems() {
      if (this.accountsTransForm.actionName !== 'GL_TRANS_FILTER') {
        this.itemsAM = []
        this.totalItemsAM = 0
      }
      this.items = []
      this.itemsGL = []
      this.itemsMD = []
      this.itemsCM = []
      this.itemsNonTrade = []
      this.totalItemsGL = 0
      this.totalItemsMD = 0
      this.totalItemsCM = 0
      this.totalItemsNonTrade = 0
    },
    setTotalItems(result) {
      if (this.accountsTransForm.actionName === 'ACCT_MASTER_FILTER') {
        this.totalItemsAM = result.data.totalElements
      } else if (this.accountsTransForm.actionName === 'GL_TRANS_FILTER') {
        this.totalItemsGL = result.data.totalElements
      } else if (this.accountsTransForm.actionName === 'MCH_DET_FILTER') {
        this.totalItemsMD = result.data.totalElements
      } else if (this.accountsTransForm.actionName === 'COMM_FILTER') {
        this.totalItemsCM = result.data.totalElements
      } else if (this.accountsTransForm.actionName === 'NON_TRADE_FILTER') {
        this.totalItemsNonTrade = result.data.totalElements
      } else {
        this.totalItems = result.data.totalElements
      }
    },
    getPagination() {
      if (this.accountsTransForm.actionName === 'ACCT_MASTER_FILTER') {
        return this.paginationAM
      } else if (this.accountsTransForm.actionName === 'GL_TRANS_FILTER') {
        return this.paginationGL
      } else if (this.accountsTransForm.actionName === 'MCH_DET_FILTER') {
        return this.paginationMD
      } else if (this.accountsTransForm.actionName === 'COMM_FILTER') {
        return this.paginationCM
      } else if (this.accountsTransForm.actionName === 'NON_TRADE_FILTER') {
        return this.paginationNonTrade
      } else {
        return this.pagination
      }
    },
    setResultItems(resultList) {
      if (this.accountsTransForm.actionName === 'ACCT_MASTER_FILTER') {
        this.itemsAM.push(resultList)
      } else if (this.accountsTransForm.actionName === 'GL_TRANS_FILTER') {
        this.itemsGL.push(resultList)
      } else if (this.accountsTransForm.actionName === 'MCH_DET_FILTER') {
        this.itemsMD.push(resultList)
      } else if (this.accountsTransForm.actionName === 'COMM_FILTER') {
        this.itemsCM.push(resultList)
      } else if (this.accountsTransForm.actionName === 'NON_TRADE_FILTER') {
        this.itemsNonTrade.push(resultList)
      } else {
        this.items.push(resultList)
      }
    },
    async getRIDetails() {
      const apiUrl = this.$store.state.accounts.menu.riDetailsList
      if (apiUrl) {
        try {
          const expand = {
            branch: this.acctMaster.branch,
            acctNo: this.acctMaster.acctNo,
            tranType: this.acctMaster.tranType,
            docNo: this.acctMaster.docNo,
            fund: this.acctMaster.fund,
            acctNoSfx: this.acctMaster.suffix
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            data.data.map(ridetail => {
              const riListDetail = {
                acctNo: replaceWithBlank(ridetail.acctNo),
                name: replaceWithBlank(ridetail.name),
                suffix: replaceWithBlank(ridetail.acctNoSfx),
                percentage: formatPrice(ridetail.riPlacePt),
                curr: replaceWithBlank(ridetail.curr),
                netAmt: formatPrice(ridetail.netAmt),
                locNetAmt: formatPrice(ridetail.locNetAmt),
                osBal: formatPrice(ridetail.osBal),
                locOsBal: formatPrice(ridetail.locOsBal)
              }
              this.riItems.push(riListDetail)
            })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getGLList() {
      const apiUrl = this.$store.state.accounts.menu.glList
      if (apiUrl) {
        try {
          const expand = {
            branch: this.acctMaster.branch,
            tranType: this.acctMaster.tranType,
            docNo: this.acctMaster.docNo,
            fund: this.acctMaster.fund,
            acctNoSfx: this.acctMaster.suffix,
            acctNo: this.acctMaster.acctNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result

          if (data.data.length > 0) {
            data.data.map(glDetail => {
              const glDetailList = {
                glAcctNo: glDetail.glAcctNoMix,
                desc: glDetail.glAcctNoDesc,
                curr: glDetail.curr,
                amt: formatPrice(glDetail.glAmt),
                locAmt: formatPrice(glDetail.glLocAmt),
                batchNo: glDetail.glBchNo,
                riPanel: glDetail.glRbcAcctNo,
                journal: glDetail.glJrnl,
                itemNo: glDetail.glItemNo,
                acctNo: glDetail.glAcctNo,
                tranDate: glDetail.glTranDate,
                acctPeriod: glDetail.glAcctPeriod,
                polOrClm: glDetail.glPolOrClm,
                tranType: glDetail.glTranType,
                docNo: glDetail.glDocNo
              }
              this.glTranItems.push(glDetailList)
            })
            this.glTransHdrDet.glJrnl = replaceWithBlank(this.glTranItems[0].journal)
            this.glTransHdrDet.acctNo = replaceWithBlank(this.glTranItems[0].acctNo)
            this.glTransHdrDet.docNo =
              replaceWithBlank(this.glTranItems[0].tranType) +
              ' ' +
              replaceWithBlank(this.documentDet.tranType2) +
              ' ' +
              replaceWithBlank(this.glTranItems[0].docNo)
            this.glTransHdrDet.tranDate = replaceWithBlank(formatDisplayDate(this.glTranItems[0].tranDate))
            this.glTransHdrDet.acctPeriod = replaceWithBlank(this.glTranItems[0].acctPeriod)
            this.glTransHdrDet.polOrClm = replaceWithBlank(this.glTranItems[0].polOrClm)
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getMatchList() {
      const apiUrl = this.$store.state.accounts.menu.matchList
      if (apiUrl) {
        try {
          const expand = {
            branch: this.acctMaster.branch,
            tranType: this.acctMaster.tranType,
            docNo: this.acctMaster.docNo,
            fund: this.acctMaster.fund,
            acctNoSfx: this.acctMaster.suffix,
            acctNo: this.acctMaster.acctNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            this.matchItems = []
            data.data.map(matchDetail => {
              const matchDetailList = {
                mdJournalNo: matchDetail.mdJrnl,
                mdMatchDate: formatDisplayDate(matchDetail.mdDate),
                mdContraType: matchDetail.mdContType,
                mdBaseDocNo: matchDetail.mdBaseDocNo,
                mdSelectedDocNo: matchDetail.mdSelectedDocNo,
                mdNetAmt: formatPrice(matchDetail.mdAmt),
                mdLocNetAmt: formatPrice(matchDetail.mdLocAmt),
                mdMatchBy: matchDetail.mdMchBy,
                mdOn: formatDisplayDate(matchDetail.mdCreatedDate),
                mdProgramId: matchDetail.mdMchProg,
                mdAcctNo: matchDetail.mdAcctNo,
                mdTranType: matchDetail.mdTranType,
                mdFund: matchDetail.mdFund,
                mdAcctNoSfx: matchDetail.mdAcctNoSfx,
                mdDocNo: matchDetail.mdDocNo,
                mdContTranType: matchDetail.mdContTranType,
                mdContDocNo: matchDetail.mdContDocNo,
                mdContJrnlNo: matchDetail.mdContJrnlNo,
                mdContBrn: matchDetail.mdContBrn
              }
              this.matchItems.push(matchDetailList)
            })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getRIMatchList() {
      const apiUrl = this.$store.state.accounts.menu.riMatchList
      if (apiUrl) {
        try {
          const expand = {
            branch: this.acctMaster.branch,
            tranType: this.acctMaster.tranType,
            docNo: this.acctMaster.docNo,
            acctNo: this.acctMaster.acctNo,
            fund: this.acctMaster.fund,
            acctNoSfx: this.acctMaster.suffix
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            this.riMatchItems = []
            data.data.map(riMatchDetail => {
              const riMatchDetailList = {
                journalNo: riMatchDetail.riMdJrnl,
                matchDate: formatDisplayDate(riMatchDetail.riMdDate),
                contraType: riMatchDetail.riMdContType,
                baseDocNo: riMatchDetail.riMdBaseDocNo,
                selectedDocNo: riMatchDetail.riMdSelectedDocNo,
                netAmt: formatPrice(riMatchDetail.riMAmt),
                locNetAmt: formatPrice(riMatchDetail.riMdLocAmt),
                matchBy: riMatchDetail.riMdMchBy,
                on: formatDisplayDate(riMatchDetail.riMdCreatedDate),
                programId: riMatchDetail.riMdMchProg
              }
              this.riMatchItems.push(riMatchDetailList)
            })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getCommPVList() {
      const apiUrl = this.$store.state.accounts.menu.commPVList
      if (apiUrl) {
        try {
          const expand = {
            branch: this.acctMaster.branch,
            tranType: this.acctMaster.tranType,
            docNo: this.acctMaster.docNo,
            fund: this.acctMaster.fund,
            acctNoSfx: this.acctMaster.suffix
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            data.data.map(commPVDetail => {
              const commPVDetailList = {
                cmType: commPVDetail.cmType,
                cmVoucher: commPVDetail.cmVoucher,
                cmBilAcctNo: commPVDetail.cmBillAcctNo,
                cmBilDocNo: commPVDetail.cmBillDocNo,
                cmOverAcctNo: commPVDetail.cmOvrdAcctNo,
                cmOverDocNo: commPVDetail.cmMixOvrdDocNo,
                cmPaidDate: formatDisplayDate(commPVDetail.relDate),
                cmOvrdBrn: commPVDetail.cmOvrdBrn,
                cmOvrdTranType: commPVDetail.cmOvrdTranType,
                cmOvrdAcctNo: commPVDetail.cmOvrdAcctNo,
                cmOvrdDocNo: commPVDetail.cmOvrdDocNo,
                cmOvrdFund: commPVDetail.cmOvrdFund,
                cmOvrdAcctNoSfx: commPVDetail.cmOvrdAcctNoSfx,
                cmPolNo: commPVDetail.polNo,
                cmRenCnt: commPVDetail.renCnt,
                cmEndtCnt: commPVDetail.endtCnt
              }
              this.commPVItems.push(commPVDetailList)
            })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchGLData(journal, itemNo) {
      const apiUrl = this.$store.state.accounts.menu.glTranDetail
      if (apiUrl) {
        try {
          const requestParams = {
            journal: journal,
            itemNo: itemNo
          }
          const response = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = response.data
          if (data) {
            this.glTransDet.journal = replaceWithBlank(data.accounts.glJrnl)
            this.glTransDet.itemNo = replaceWithBlank(data.accounts.glItemNo)
            this.glTransDet.glAcctNo =
              replaceWithBlank(data.accounts.glBranch) +
              ' ' +
              replaceWithBlank(data.accounts.glGlAcctNo) +
              ' ' +
              replaceWithBlank(data.accounts.glFund) +
              ' ' +
              replaceWithBlank(data.accounts.suffix)
            this.glTransDet.gstStatRate = replaceWithBlank(data.accounts.glGstStat) + ' ' + formatPrice(data.accounts.glGstRate)
            this.glTransDet.gstAmt = formatPrice(data.accounts.glGstAmt)
            this.glTransDet.origCurrAmt = data.accounts.curr + ' ' + formatPrice(data.accounts.glAmt)
            this.glTransDet.locAmt = formatPrice(data.accounts.glLocAmt)
            this.glTransDet.transDate = replaceWithBlank(formatDisplayDate(data.accounts.glTranDate))
            this.glTransDet.acctPeriod = data.accounts.glAcctMth + ' ' + data.accounts.glAcctYr
            this.glTransDet.batchNo = replaceWithBlank(data.accounts.glBchNo)
            this.glTransDet.reversalStat = replaceWithBlank(data.accounts.revStat)
            this.glTransDet.docBrn = replaceWithBlank(data.accounts.glDocBrn)
            this.glTransDet.docFund = replaceWithBlank(data.accounts.glDocFund)
            this.glTransDet.docNo = replaceWithBlank(data.accounts.glTranType) + ' ' + replaceWithBlank(data.accounts.glDocNo)
            this.glTransDet.docAcctSfx = replaceWithBlank(data.accounts.glDocAcctNoSfx)
            this.glTransDet.acctNo = replaceWithBlank(data.accounts.glAcctNo)
            this.glTransDet.rbcAcctNo = replaceWithBlank(data.accounts.glRbcAcctNo)
            this.glTransDet.polNo =
              replaceWithBlank(data.accounts.glPolNo) +
              ' ' +
              replaceWithBlank(data.accounts.glRenCnt) +
              ' ' +
              replaceWithBlank(data.accounts.glEndtCnt)
            this.glTransDet.clmNo = replaceWithBlank(data.accounts.glClmNo)
            this.glTransDet.comDate = replaceWithBlank(formatDisplayDate(data.accounts.glComDate))
            this.glTransDet.expDate = replaceWithBlank(formatDisplayDate(data.accounts.glExpiryDate))
            this.glTransDet.holdGLUpd = replaceWithBlank(data.accounts.glHoldGLUpd)
            this.glTransDet.glSys = replaceWithBlank(data.accounts.glSys)
            this.glTransDet.glAc1 = replaceWithBlank(data.accounts.glAc1)
            this.glTransDet.glAc2 = replaceWithBlank(data.accounts.glAc2)
            this.glTransDet.glAc3 = replaceWithBlank(data.accounts.glAc3)
            this.glTransDet.glAc4 = replaceWithBlank(data.accounts.glAc4)
            this.glTransDet.glAc5 = replaceWithBlank(data.accounts.glAc5)
            this.glTransDet.glAc6 = replaceWithBlank(data.accounts.glAc6)
            this.glTransDet.glAc7 = replaceWithBlank(data.accounts.glAc7)
            this.glTransDet.glAc8 = replaceWithBlank(data.accounts.glAc8)
            this.glTransDet.glAc9 = replaceWithBlank(data.accounts.glAc9)
            this.glTransDet.glAc10 = replaceWithBlank(data.accounts.glAc10)
            this.glTransDet.createdBy = replaceWithBlank(data.accounts.glCreatedByName)
            if (data.accounts.glCreatedDate !== null) {
              this.glTransDet.createdBy = this.glTransDet.createdBy + ' on ' + formatDisplayDateTime2(data.accounts.glCreatedDate)
            }
            this.glTransDet.createdBy = this.glTransDet.createdBy + ' ' + replaceWithBlank(data.accounts.glCreatedProgId)

            this.glTransDet.modifiedBy = replaceWithBlank(data.accounts.glModifiedByName)
            if (data.accounts.glModifiedDate !== null) {
              this.glTransDet.modifiedBy = this.glTransDet.modifiedBy + ' on ' + formatDisplayDateTime2(data.accounts.glModifiedDate)
            }
            this.glTransDet.modifiedBy = this.glTransDet.modifiedBy + ' ' + replaceWithBlank(data.accounts.glModifiedProId)
            this.glTransDet.glApplnInt = replaceWithBlank(data.accounts.glApplnInt)
            this.glTransDet.glInterface = this.glTransDet.glApplnInt
            for (var a = 0; a < this.glInterface.length; a++) {
              if (this.glInterface[a].value === this.glTransDet.glApplnInt) {
                this.glTransDet.glInterface = this.glInterface[a].label
              }
            }
            this.glTransDet.glTranType = data.accounts.glTranType
            this.glTransDet.project = data.accounts.project
            this.glTransDet.glDept = data.accounts.glDept
            this.glTransDet.gstStat = replaceWithBlank(data.accounts.glGstStat)
            this.glTransDet.gstRate = formatPrice(data.accounts.glGstRate)
            this.$store.commit(GLTRAN_DETAIL, this.glTransDet)
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchMatchData(params) {
      const apiUrl = this.$store.state.accounts.menu.matchDetail
      if (apiUrl) {
        try {
          const requestParams = {
            acctNo: params.mdAcctNo,
            tranType: params.mdTranType,
            fund: params.mdFund,
            acctNoSfx: params.mdAcctNoSfx,
            docNo: params.mdDocNo,
            contTranType: params.mdContTranType,
            contDocNo: params.mdContDocNo,
            contJrnlNo: params.mdContJrnlNo,
            contBrn: params.mdContBrn
          }
          const response = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = response.data
          if (data) {
            this.matchDet.journal = replaceWithBlank(data.accounts.mdJrnl)
            this.matchDet.acctNo = replaceWithBlank(data.accounts.acctNo)
            this.matchDet.matchedDate = replaceWithBlank(formatDisplayDate(data.accounts.mdDate))
            this.matchDet.baseTranReceiptLbl = replaceWithBlank(data.accounts.mdBaseTransLbl)
            this.matchDet.baseTranReceipt = replaceWithBlank(data.accounts.mdBaseTrans)
            this.matchDet.contBranch = replaceWithBlank(data.accounts.mdContBrn)
            this.matchDet.selTranPaymentLbl = replaceWithBlank(data.accounts.mdSelectTransLbl)
            this.matchDet.selTranPayment = replaceWithBlank(data.accounts.mdSelectTrans)
            this.matchDet.branch = replaceWithBlank(data.accounts.mdBranch)
            this.matchDet.matchedAmt = formatPrice(data.accounts.mdAmt)
            this.matchDet.matchedLocAmt = formatPrice(data.accounts.mdLocAmt)
            this.matchDet.mchListPrtd = data.accounts.mchListPrtd
            if (data.accounts.mdCreatedbyName !== null) {
              this.matchDet.createdBy = data.accounts.mdCreatedbyName
            }
            if (data.accounts.mdCreatedDate !== null) {
              this.matchDet.createdBy = this.matchDet.createdBy + ' on ' + formatDisplayDateTime2(data.accounts.mdCreatedDate)
            }
            if (data.accounts.mdMchProg !== null) {
              this.matchDet.createdBy = this.matchDet.createdBy + ' ' + data.accounts.mdMchProg
            }
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchCommPVData(params) {
      const apiUrl = this.$store.state.accounts.menu.commPVDetail
      if (apiUrl) {
        try {
          const requestParams = {
            commType: params.cmType,
            voucherNo: params.cmVoucher,
            ovrdBrn: params.cmOvrdBrn,
            ovrdAcctNo: params.cmOvrdAcctNo,
            ovrdTranType: params.cmOvrdTranType,
            ovrdDocNo: params.cmOvrdDocNo,
            ovrdFund: params.cmOvrdFund,
            ovrdAcctNoSfx: params.cmOvrdAcctNoSfx,
            polNo: params.cmPolNo,
            renCnt: params.cmRenCnt,
            endtCnt: params.cmEndtCnt
          }
          const response = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = response.data
          if (data) {
            this.comVoucherDet.comType = replaceWithBlank(data.accounts.cmType)
            this.comVoucherDet.voucherNo = replaceWithBlank(data.accounts.cmVoucher)
            this.comVoucherDet.comPayRecover = formatPrice(data.accounts.cmCommPayRcvr)
            this.comVoucherDet.comDel = replaceWithBlank(data.accounts.cmRecDel)
            if (data.accounts.cmMatchedByName !== null) {
              this.comVoucherDet.comMatchedBy = data.accounts.cmMatchedByName
            }
            if (data.accounts.cmRelDate !== null) {
              this.comVoucherDet.comMatchedBy = this.comVoucherDet.comMatchedBy + ' on ' + formatDisplayDate(data.accounts.cmRelDate)
            }
            this.comVoucherDet.bAcctNo = replaceWithBlank(data.accounts.cmBillAcctNo)
            this.comVoucherDet.oAcctNo = replaceWithBlank(data.accounts.cmOvrdAcctNo)
            this.comVoucherDet.bBranch = replaceWithBlank(data.accounts.cmBillAcctBrn)
            this.comVoucherDet.oBranch = replaceWithBlank(data.accounts.cmOvrdBrn)
            this.comVoucherDet.bTranType = replaceWithBlank(data.accounts.cmBillAcctTranType)
            this.comVoucherDet.oTranType = replaceWithBlank(data.accounts.cmOvrdTranType)
            this.comVoucherDet.bDocNo = replaceWithBlank(data.accounts.cmBillAcctDocNo)
            this.comVoucherDet.oDocNo = replaceWithBlank(data.accounts.cmOvrdDocNo)
            this.comVoucherDet.bFund = replaceWithBlank(data.accounts.cmBillAcctFund)
            this.comVoucherDet.oFund = replaceWithBlank(data.accounts.cmOvrdFund)
            this.comVoucherDet.bSuffix = replaceWithBlank(data.accounts.cmBillAcctNoSfx)
            this.comVoucherDet.oSuffix = replaceWithBlank(data.accounts.cmOvrdAcctNoSfx)
            this.comVoucherDet.bComm = formatPrice(data.accounts.cmBillAcctComm)
            this.comVoucherDet.oComm = formatPrice(data.accounts.cmOvrdComm)
            this.comVoucherDet.bOutStdBal = formatPrice(data.accounts.cmBillAcctOSBal)
            this.comVoucherDet.oOutStdBal = formatPrice(data.accounts.cmOvrdOSBal)
            this.comVoucherDet.polNo = replaceWithBlank(data.accounts.cmPolNo)
            this.comVoucherDet.lastMatchedDate = replaceWithBlank(formatDisplayDate(data.accounts.cmLastMchDate))
            this.comVoucherDet.tranDate = replaceWithBlank(formatDisplayDate(data.accounts.cmTranDate))
            this.comVoucherDet.prem = formatPrice(data.accounts.cmPremium)
            this.comVoucherDet.netAmt = formatPrice(data.accounts.cmNetAmt)
            this.comVoucherDet.insRefNoName = replaceWithBlank(data.accounts.cmInsRefNoName)
            if (data.accounts.cmCreatedByName !== null) {
              this.comVoucherDet.createdBy = data.accounts.cmCreatedByName
            }
            if (data.accounts.cmCreatedDate !== null) {
              this.comVoucherDet.createdBy = this.comVoucherDet.createdBy + ' on ' + formatDisplayDate(data.accounts.cmCreatedDate)
            }

            if (data.accounts.cmModifiedByName !== null) {
              this.comVoucherDet.modifiedBy = data.accounts.cmModifiedByName
            }

            if (data.accounts.cmModifiedDate !== null) {
              this.comVoucherDet.modifiedBy = this.comVoucherDet.modifiedBy + ' on ' + formatDisplayDate(data.accounts.cmModifiedDate)
            }
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async doChkBatch(params) {
      this.accountsTransForm.matchBatchProcess = false
      const apiUrl = this.$store.state.accounts.menu.matchBatchProcess
      if (apiUrl) {
        try {
          const requestParams = {
            branch: params.branch,
            acctNo: params.acctNo,
            tranType: params.type,
            docNo: params.docNo,
            fund: params.fund,
            acctNoSfx: params.suffix
          }
          const result = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = result.data
          if (data) {
            this.accountsTransForm.matchBatchProcess = true
            this.accountsTransForm.matchCreatedProgId = data.createdProgId
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async doChkGLTran(params) {
      this.accountsTransForm.glTranFound = false
      const apiUrl = this.$store.state.accounts.menu.glTranBchNo
      if (apiUrl) {
        try {
          const requestParams = {
            branch: params.branch,
            tranType: params.type,
            docNo: params.docNo,
            acctNo: params.acctNo,
            acctNoSfx: params.suffix
          }
          const result = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = result.data
          if (data) {
            this.accountsTransForm.glTranFound = true
            this.accountsTransForm.glTranBchNo = data.bchNo
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async fetchDelData(infor) {
      const apiUrl = this.$store.state.accounts.menu.deleteAccount
      try {
        const requestParams = infor
        if (apiUrl) {
          this.$http
            .post(apiUrl, requestParams)
            .then(result => {
              const data = result.data
              this.notify(data)
              this.doClearFilters()
            })
            .catch(err => {
              const { data } = err.response
              if (data) {
                this.notify(data)
              }
            })
        }
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.notify(data)
        }
      }
    },
    async fetchBranchWBranchUserId() {
      this.branches = []
      let apiUrl = this.$store.state.accounts.menu.fetchBranchWBranchUserId
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBranch() {
      this.allbranches = []
      let apiUrl = this.$store.state.accounts.menu.fetchBranch
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.allbranches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchDepartment() {
      this.departments = []
      let apiUrl = this.$store.state.accounts.menu.fetchDepartment
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.departments.push(data)
              })
            }
          }
        })
      }
    },
    async fetchCurrency() {
      this.currencies = []
      let apiUrl = this.$store.state.accounts.menu.fetchCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.currencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchFund() {
      this.funds = []
      let apiUrl = this.$store.state.accounts.menu.fetchFund
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.funds.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAcctNoSfx() {
      this.acctnosuffixes = []
      let apiUrl = this.$store.state.accounts.menu.fetchAcctNoSfx
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.acctnosuffixes.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAccount() {
      this.accountnos = []
      let apiUrl = this.$store.state.accounts.menu.fetchAccount
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.accountnos.push(data)
              })
            }
          }
        })
      }
    },
    async fetchGstStatus() {
      this.gstStatuses = []
      let apiUrl = this.$store.state.accounts.menu.fetchGstStatus
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.gstStatuses.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBank() {
      this.banks = []
      let apiUrl = this.$store.state.accounts.menu.fetchBank
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.banks.push(data)
              })
            }
          }
        })
      }
    },
    async fetchPaymentMethod() {
      this.paymentmethods = []
      let apiUrl = this.$store.state.accounts.menu.fetchPaymentMethod
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.paymentmethods.push(data)
              })
            }
          }
        })
      }
    },
    async fetchPayerBank() {
      this.payerbanks = []
      let apiUrl = this.$store.state.accounts.menu.fetchPayerBank
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.payerbanks.push(data)
              })
            }
          }
        })
      }
    },
    async fetchPolicyType() {
      this.policytypes = []
      let apiUrl = this.$store.state.accounts.menu.fetchPolicyType
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.policytypes.push(data)
              })
            }
          }
        })
      }
    },
    async fetchGlAnalysisCode() {
      this.glanalysiscodes = []
      let apiUrl = this.$store.state.accounts.menu.fetchGlAnalysisCode
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.glanalysiscodes.push(data)
              })
            }
          }
        })
      }
    },
    async fetchGlDepartment() {
      this.glDepartments = []
      let apiUrl = this.$store.state.accounts.menu.fetchGlDepartment
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.glDepartments.push(data)
              })
            }
          }
        })
      }
    },
    checkFiltersNullValue() {
      const ignore = ['transactions', 'actions', 'actionName', 'acctPageNo', 'acctPageSize', 'applyFilters', 'seqNo', 'matchBatchProcess',
        'matchCreatedProgId', 'bankTranDate', 'glTranFound', 'glTranBchNo', 'delAcct', 'includeBalance', 'includeVoidTransactions',
        'enableDueDate', 'enableLastMatchedDate']
      return Object.keys(this.accountsTransForm)
        .filter(key => !ignore.includes(key))
        .every(key => this.accountsTransForm[key] === null ||
          this.accountsTransForm[key] === '' ||
          this.accountsTransForm[key] === undefined
        )
    },
    doExportFilters() {
      const checknull = this.checkFiltersNullValue()
      if (checknull) {
        this.exportList.promptFilterWarning = true
      } else {
        this.exportList.promptFilterWarning = false
        this.exportList.cfmExportAcctList = true
      }
    },
    async doExportList(columns, columnsTitle) {
      const apiUrl = this.$store.state.accounts.menu.exportAcctFilteredList
      if (apiUrl) {
        try {
          let massageForm = clone(this.accountsTransForm)
          massageForm.columns = columns
          massageForm.columnsTitle = columnsTitle
          massageForm.detailTabs = this.getDetTab
          await this.$http.post(apiUrl, massageForm)
          this.processing = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.processing = false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    getDocumentNo(oriDocNo, updateDocNo, actionName) {
      if (actionName === 'CREATE_MATCH_LATER') {
        return oriDocNo
      }
      return updateDocNo
    }
  },
  mixins: [MessageMixin]
}
